/**
 * ACTIVITY-CORE — domain hooks for the activity feed and per-entity history
 * (the "transaction history"), and for the trash (L2). Same
 * pattern as `use-finance-query.ts`: they encapsulate queryKey + query builder.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { ActivityRecent, Category, PowerSyncDrizzleDB, Tx, Wallet } from "@cashsync/powersync";

import {
  type ActivityRow,
  getActivityFeedQuery,
  getEntityActivityQuery,
  getTrashedCategoriesQuery,
  getTrashedTransactionsQuery,
  getTrashedWalletsQuery,
  queryKeys,
} from "../index";

import { createPowerSyncQuery, DEFAULT_THROTTLE_MS } from "./create-power-sync-query";

interface BaseActivityQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

// The activity_recent schema row structurally matches ActivityRow.
type ActivityRecord = ActivityRecent & ActivityRow;

/** Global activity feed (activity_recent bucket, ~90 days). */
export function useActivityFeedQuery(options: BaseActivityQueryOptions & { limit?: number }) {
  return createPowerSyncQuery<ActivityRecord>({
    queryKey: () => queryKeys.activity.feed(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      return db ? getActivityFeedQuery(db, { limit: options.limit }) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

/** Local history for an entity (transaction/wallet/category/budget). */
export function useEntityActivityQuery(
  options: BaseActivityQueryOptions & {
    entityType: Accessor<string>;
    entityId: Accessor<string>;
    limit?: number;
    enabled?: Accessor<boolean>;
  },
) {
  return createPowerSyncQuery<ActivityRecord>({
    queryKey: () =>
      queryKeys.activity.entity(options.userId(), options.entityType(), options.entityId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      return db
        ? getEntityActivityQuery(db, options.entityType(), options.entityId(), {
            limit: options.limit,
          })
        : null;
    },
    enabled: () => (options.enabled ? options.enabled() : true) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

// ─── Trash (L2) ────────────────────────────────────────────────────────────────

export function useTrashedTransactionsQuery(options: BaseActivityQueryOptions) {
  return createPowerSyncQuery<Tx>({
    queryKey: () => queryKeys.trash.transactions(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getTrashedTransactionsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

export function useTrashedWalletsQuery(options: BaseActivityQueryOptions) {
  return createPowerSyncQuery<Wallet>({
    queryKey: () => queryKeys.trash.wallets(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getTrashedWalletsQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}

export function useTrashedCategoriesQuery(options: BaseActivityQueryOptions) {
  return createPowerSyncQuery<Category>({
    queryKey: () => queryKeys.trash.categories(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getTrashedCategoriesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}
